/**
 * atelier-audio.js — the procedural Greek soundscape of the atelier.
 *
 * Everything is synthesized with WebAudio — zero audio files, nothing
 * to license. The aim is atmosphere, not music: wind through columns,
 * a distant lyre, bees in the garden. Every gain is conservative and
 * every timing randomized so nothing ever loops audibly.
 *
 * Graph:  generators → layer gains → bed bus ┐
 *         one-shot events → fx bus           ├→ compressor → master gain → out
 *
 * Public contract (REVISION_BRIEF §2a) — always installed, never throws:
 *   window.AtelierAudio = { ready, enabled(), toggle(), setScene(name),
 *                           setIntensity(v), event(name) }
 *
 * Lifecycle: OFF by default; preference in localStorage['atl-audio'];
 * the AudioContext is created/resumed only from a real user gesture;
 * document.hidden suspends, visibility restores; missing WebAudio
 * degrades to a silent no-op with the same API surface.
 */
(() => {
  'use strict';
  if (window.AtelierAudio && window.AtelierAudio.ready) return;

  const AC = window.AudioContext || window.webkitAudioContext;
  const doc = document;
  const rootEl = doc.documentElement;
  const KEY = 'atl-audio';
  const MASTER_LEVEL = 0.72;

  const rnd = (a, b) => a + Math.random() * (b - a);
  const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
  const clamp01 = (v) => Math.min(1, Math.max(0, Number(v) || 0));

  const readPref = () => {
    try {
      return window.localStorage.getItem(KEY) === 'on';
    } catch (e) {
      return false;
    }
  };
  const writePref = (on) => {
    try {
      window.localStorage.setItem(KEY, on ? 'on' : 'off');
    } catch (e) {
      /* private mode — session-only preference */
    }
  };
  const announce = (msg) => {
    try {
      if (window.Atelier && window.Atelier.helpers) window.Atelier.helpers.announce(msg);
    } catch (e) {}
  };

  /* ---------------- state ---------------- */
  let prefOn = readPref();
  let gestured = false; // a real user gesture has happened
  let ctx = null;
  let master = null;
  let bedBus = null;
  let fxBus = null;
  let noiseBuf = null;
  let currentScene = 'none';
  let bed = null; // { name, out, layers[] }
  let retiring = []; // beds fading away
  let intensity = 0.5;
  let compNode = null;
  let clipNode = null;
  let clipCurve = null;
  let guardAn = null;
  let guardBuf = null;
  let guardHot = 0;
  let guardTimer = 0;

  /* ---------------- tiny audio helpers ---------------- */
  const now = () => ctx.currentTime;
  const running = () => !!(ctx && ctx.state === 'running' && prefOn);
  const ramp = (param, v, sec) => {
    const t = now();
    param.cancelScheduledValues(t);
    param.setValueAtTime(param.value, t);
    param.linearRampToValueAtTime(v, t + sec);
  };
  const stopNode = (n) => {
    try {
      n.stop();
    } catch (e) {}
  };
  const quietPromise = (p) => {
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };

  /* Randomized self-rescheduling timer. The callback is skipped (but the
     chain keeps breathing) while the context is not audibly running, so
     suspended tabs never pile up scheduled sound. Returns a kill fn. */
  const loopTimer = (fn, minMs, maxMs, immediate) => {
    let id = 0;
    let dead = false;
    const fire = () => {
      if (dead) return;
      if (running() && !doc.hidden) {
        try {
          fn();
        } catch (e) {}
      }
    };
    const next = (delay) => {
      id = window.setTimeout(() => {
        fire();
        next(rnd(minMs, maxMs));
      }, delay);
    };
    next(immediate ? rnd(120, 700) : rnd(minMs, maxMs));
    return () => {
      dead = true;
      window.clearTimeout(id);
    };
  };

  /* One shared 2-second pink-ish noise loop (leaky integrator stack). */
  const getNoise = () => {
    if (noiseBuf) return noiseBuf;
    const len = Math.floor(ctx.sampleRate * 2);
    noiseBuf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = noiseBuf.getChannelData(0);
    let b0 = 0;
    let b1 = 0;
    let b2 = 0;
    for (let i = 0; i < len; i++) {
      const w = Math.random() * 2 - 1;
      b0 = 0.997 * b0 + 0.0299 * w;
      b1 = 0.985 * b1 + 0.0325 * w;
      b2 = 0.95 * b2 + 0.0481 * w;
      d[i] = (b0 + b1 + b2 + w * 0.05) * 2.1;
    }
    return noiseBuf;
  };
  const noiseSrc = () => {
    const s = ctx.createBufferSource();
    s.buffer = getNoise();
    s.loop = true;
    s.start();
    return s;
  };

  /* D Dorian across three octaves — the lyre's whole vocabulary. */
  const SCALE = (() => {
    const steps = [0, 2, 3, 5, 7, 9, 10];
    const out = [];
    for (let o = 0; o < 3; o++) steps.forEach((s) => out.push(s + 12 * o));
    return out;
  })();
  const noteHz = (base, i) =>
    base * Math.pow(2, SCALE[Math.max(0, Math.min(SCALE.length - 1, i))] / 12);

  /* A plucked string, synthesized additively: a fingernail transient and
     a few exponentially-damped partials. Deliberately loop-free — the
     earlier delay-in-a-feedback-cycle Karplus string screamed at full
     scale in Chromium whenever the delay fell under one render quantum
     (any note above ~345 Hz), so no synthesis here may ever feed back. */
  const ksPluck = (dest, freq, level, bright, when) => {
    const t0 = Math.max(when || now(), now());
    const out = ctx.createGain();
    out.gain.value = 1;
    out.connect(dest);
    const brightness = Math.max(0.3, Math.min(1, (bright || 2400) / 3200));
    const partials = [
      { m: 1, lv: 1, dec: 1.9 },
      { m: 2, lv: 0.4 * brightness, dec: 1.1 },
      { m: 2.997, lv: 0.18 * brightness, dec: 0.72 },
      { m: 4.02, lv: 0.08 * brightness, dec: 0.46 },
    ];
    const nyquist = ctx.sampleRate * 0.45;
    partials.forEach((p) => {
      if (freq * p.m > nyquist) return;
      const osc = ctx.createOscillator();
      osc.type = p.m === 1 ? 'triangle' : 'sine';
      osc.frequency.setValueAtTime(freq * p.m, t0);
      osc.detune.setValueAtTime(rnd(-5, 5), t0);
      const g = ctx.createGain();
      const lv = Math.max(level * p.lv, 0.0002);
      g.gain.setValueAtTime(0, t0);
      g.gain.linearRampToValueAtTime(lv, t0 + 0.004);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + Math.max(0.14, p.dec));
      osc.connect(g);
      g.connect(out);
      osc.start(t0);
      osc.stop(t0 + p.dec + 0.06);
    });
    tap(out, {
      freq: Math.min(freq * 6, 5200),
      q: 1.1,
      dur: 0.02,
      level: level * 0.5,
      attack: 0.001,
      at: t0 - now(),
    });
    window.setTimeout(() => {
      try {
        out.disconnect();
      } catch (e) {}
    }, (t0 - now()) * 1000 + 2400);
  };

  /* A small FM bird — songbird syllables or a longer falling gull. */
  const chirp = (dest, o) => {
    o = o || {};
    const t0 = now() + (o.at || 0);
    const f0 = o.gull ? rnd(760, 1150) : rnd(1900, 3100);
    const syllables = o.gull ? 1 : 1 + Math.floor(Math.random() * 3);
    for (let i = 0; i < syllables; i++) {
      const t = t0 + i * rnd(0.14, 0.24);
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      const f = f0 * rnd(0.92, 1.1);
      osc.frequency.setValueAtTime(f, t);
      if (o.gull) {
        osc.frequency.linearRampToValueAtTime(f * 0.72, t + 0.28);
      } else {
        osc.frequency.linearRampToValueAtTime(f * rnd(1.15, 1.42), t + 0.045);
        osc.frequency.linearRampToValueAtTime(f * rnd(0.85, 0.98), t + 0.1);
      }
      const lv = (o.level || 0.045) * rnd(0.55, 1);
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(lv, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + (o.gull ? 0.32 : 0.13));
      osc.connect(g);
      g.connect(dest);
      osc.start(t);
      osc.stop(t + (o.gull ? 0.38 : 0.18));
    }
  };

  /* Short band-passed noise tap — footsteps, chisel ticks. */
  const tap = (dest, o) => {
    o = o || {};
    const t0 = now() + (o.at || 0);
    const src = ctx.createBufferSource();
    src.buffer = getNoise();
    src.playbackRate.value = rnd(0.9, 1.12);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = (o.freq || 1200) * rnd(0.92, 1.08);
    bp.Q.value = o.q || 1;
    const g = ctx.createGain();
    const lv = (o.level || 0.05) * rnd(0.75, 1);
    const dur = o.dur || 0.06;
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(lv, t0 + (o.attack || 0.005));
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(bp);
    bp.connect(g);
    g.connect(dest);
    src.start(t0, rnd(0, 1.2), dur + 0.12);
  };

  /* A brush swish: noise breathed through a sweeping bandpass. */
  const swish = (dest, dur, level) => {
    const t0 = now();
    const d = dur || rnd(0.3, 0.45);
    const src = ctx.createBufferSource();
    src.buffer = getNoise();
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.Q.value = 0.8;
    bp.frequency.setValueAtTime(rnd(380, 520), t0);
    bp.frequency.linearRampToValueAtTime(rnd(1300, 1750), t0 + d * 0.45);
    bp.frequency.linearRampToValueAtTime(rnd(520, 760), t0 + d);
    const g = ctx.createGain();
    const lv = level || 0.05;
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(lv, t0 + d * 0.35);
    g.gain.linearRampToValueAtTime(0, t0 + d);
    src.connect(bp);
    bp.connect(g);
    g.connect(dest);
    src.start(t0, rnd(0, 1.2), d + 0.1);
  };

  /* A single sea swell (used by the waves bed and the 'wave' event). */
  const seaSwell = (dest, o) => {
    o = o || {};
    const t0 = now();
    const src = ctx.createBufferSource();
    src.buffer = getNoise();
    src.loop = true;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.Q.value = 0.5;
    lp.frequency.setValueAtTime(o.freq || 420, t0);
    lp.frequency.linearRampToValueAtTime((o.freq || 420) * rnd(1.7, 2.3), t0 + rnd(1.4, 2));
    lp.frequency.linearRampToValueAtTime(o.freq || 420, t0 + rnd(3.6, 4.6));
    const g = ctx.createGain();
    const lv = o.level || 0.06;
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(lv, t0 + rnd(1.4, 2));
    g.gain.linearRampToValueAtTime(0, t0 + rnd(3.8, 4.8));
    src.connect(lp);
    lp.connect(g);
    g.connect(dest);
    src.start(t0, rnd(0, 1.5));
    window.setTimeout(() => stopNode(src), 5200);
  };

  /* ---------------- bed generators ----------------
     Each returns { out: AudioNode, stop() }. Recipes below mix them. */
  const GEN = {
    /* Filtered noise breathing on two incommensurate LFOs. */
    wind(o) {
      o = o || {};
      const out = ctx.createGain();
      const depth = o.depth == null ? 0.35 : o.depth;
      out.gain.value = 0.62 - depth * 0.5;
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = o.freq || 320;
      lp.Q.value = 0.7;
      const src = noiseSrc();
      src.connect(lp);
      lp.connect(out);
      const lfo = ctx.createOscillator();
      lfo.frequency.value = o.rate || rnd(0.05, 0.09);
      const la = ctx.createGain();
      la.gain.value = depth * 0.5;
      lfo.connect(la);
      la.connect(out.gain);
      const lfo2 = ctx.createOscillator();
      lfo2.frequency.value = (o.rate || 0.07) * 0.617;
      const la2 = ctx.createGain();
      la2.gain.value = (o.freq || 320) * 0.45;
      lfo2.connect(la2);
      la2.connect(lp.frequency);
      lfo.start();
      lfo2.start();
      return {
        out,
        stop() {
          [src, lfo, lfo2].forEach(stopNode);
          try {
            out.disconnect();
          } catch (e) {}
        },
      };
    },

    /* Slow noise swells every 7–11 s — the sea breathing. */
    waves(o) {
      o = o || {};
      const out = ctx.createGain();
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = o.freq || 460;
      lp.Q.value = 0.5;
      const swell = ctx.createGain();
      swell.gain.value = 0.05;
      const src = noiseSrc();
      src.connect(lp);
      lp.connect(swell);
      swell.connect(out);
      const kill = loopTimer(
        () => {
          const t = now();
          const peak = rnd(0.4, 0.8) * (o.big ? 1.2 : 1);
          swell.gain.cancelScheduledValues(t);
          swell.gain.setValueAtTime(swell.gain.value, t);
          swell.gain.linearRampToValueAtTime(peak, t + rnd(1.8, 2.8));
          swell.gain.linearRampToValueAtTime(0.05, t + rnd(4.6, 6.4));
          lp.frequency.cancelScheduledValues(t);
          lp.frequency.setValueAtTime(lp.frequency.value, t);
          lp.frequency.linearRampToValueAtTime((o.freq || 460) * rnd(1.5, 2.1), t + rnd(1.6, 2.4));
          lp.frequency.linearRampToValueAtTime(o.freq || 460, t + rnd(4.5, 6));
        },
        7000,
        11000,
        true
      );
      return {
        out,
        stop() {
          kill();
          stopNode(src);
          try {
            out.disconnect();
          } catch (e) {}
        },
      };
    },

    /* High-shelf shimmer with irregular gusts — leaves. */
    leaves(o) {
      o = o || {};
      const out = ctx.createGain();
      out.gain.value = 0.35;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = o.freq || 3900;
      bp.Q.value = 0.8;
      const src = noiseSrc();
      src.connect(bp);
      bp.connect(out);
      const lfo = ctx.createOscillator();
      lfo.frequency.value = rnd(0.22, 0.4);
      const la = ctx.createGain();
      la.gain.value = 0.14;
      lfo.connect(la);
      la.connect(out.gain);
      lfo.start();
      const kill = loopTimer(
        () => {
          out.gain.setTargetAtTime(rnd(0.2, 0.62), now(), 0.9);
        },
        2600,
        8000,
        false
      );
      return {
        out,
        stop() {
          kill();
          stopNode(src);
          stopNode(lfo);
          try {
            out.disconnect();
          } catch (e) {}
        },
      };
    },

    /* Sparse gentle FM chirps; o.gull turns songbirds into gulls. */
    birds(o) {
      o = o || {};
      const out = ctx.createGain();
      out.gain.value = 1;
      const kill = loopTimer(
        () => {
          chirp(out, { gull: o.gull, level: o.level || 0.045 });
          if (!o.gull && Math.random() < 0.3) {
            chirp(out, { level: (o.level || 0.045) * 0.7, at: rnd(0.5, 1.1) });
          }
        },
        o.minMs || 4200,
        o.maxMs || (o.gull ? 9500 : 13000),
        false
      );
      return {
        out,
        stop() {
          kill();
          try {
            out.disconnect();
          } catch (e) {}
        },
      };
    },

    /* Very low detuned saws under a dark lowpass — a bee hum. */
    bees(o) {
      o = o || {};
      const out = ctx.createGain();
      out.gain.value = 0.5;
      const g = ctx.createGain();
      g.gain.value = 0.11;
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 260;
      lp.Q.value = 1;
      const f = o.freq || 168;
      const o1 = ctx.createOscillator();
      o1.type = 'sawtooth';
      o1.frequency.value = f;
      const o2 = ctx.createOscillator();
      o2.type = 'sawtooth';
      o2.frequency.value = f * 1.011;
      const wob = ctx.createOscillator();
      wob.frequency.value = rnd(0.14, 0.3);
      const wa = ctx.createGain();
      wa.gain.value = 0.32;
      wob.connect(wa);
      wa.connect(out.gain);
      const wa2 = ctx.createGain();
      wa2.gain.value = 9;
      wob.connect(wa2);
      wa2.connect(o2.detune);
      o1.connect(g);
      o2.connect(g);
      g.connect(lp);
      lp.connect(out);
      o1.start();
      o2.start();
      wob.start();
      return {
        out,
        stop() {
          [o1, o2, wob].forEach(stopNode);
          try {
            out.disconnect();
          } catch (e) {}
        },
      };
    },

    /* Karplus-Strong lyre phrases wandering a Dorian ladder. */
    lyre(o) {
      o = o || {};
      const out = ctx.createGain();
      out.gain.value = 1;
      let idx = 4 + Math.floor(Math.random() * 8);
      const kill = loopTimer(
        () => {
          const notes = 1 + Math.floor(Math.random() * 3);
          for (let i = 0; i < notes; i++) {
            idx = Math.max(0, Math.min(SCALE.length - 1, idx + pick([-3, -2, -1, 1, 2, 3])));
            ksPluck(
              out,
              noteHz(o.base || 146.83, idx),
              0.07 * rnd(0.65, 1),
              o.bright || 2400,
              now() + i * rnd(0.32, 0.62)
            );
          }
        },
        o.minMs || 6000,
        o.maxMs || 16000,
        true
      );
      return {
        out,
        stop() {
          kill();
          try {
            out.disconnect();
          } catch (e) {}
        },
      };
    },

    /* Barely-there detuned sine choir through a very slow bandpass. */
    choir(o) {
      o = o || {};
      const out = ctx.createGain();
      out.gain.value = 0.5;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 520;
      bp.Q.value = 0.8;
      bp.connect(out);
      const nodes = [];
      const freqs = o.freqs || [146.83, 220, 293.66, 349.23];
      freqs.forEach((f) => {
        const osc = ctx.createOscillator();
        osc.frequency.value = f;
        osc.detune.value = rnd(-6, 6);
        const g = ctx.createGain();
        g.gain.value = rnd(0.15, 0.25);
        const drift = ctx.createOscillator();
        drift.frequency.value = rnd(0.04, 0.1);
        const da = ctx.createGain();
        da.gain.value = rnd(2.5, 5);
        drift.connect(da);
        da.connect(osc.detune);
        osc.connect(g);
        g.connect(bp);
        osc.start();
        drift.start();
        nodes.push(osc, drift);
      });
      const sweep = ctx.createOscillator();
      sweep.frequency.value = 0.023;
      const sa = ctx.createGain();
      sa.gain.value = 240;
      sweep.connect(sa);
      sa.connect(bp.frequency);
      sweep.start();
      nodes.push(sweep);
      return {
        out,
        stop() {
          nodes.forEach(stopNode);
          try {
            out.disconnect();
          } catch (e) {}
        },
      };
    },

    /* Long low temple-stone resonance with a slow interference beat. */
    stone(o) {
      o = o || {};
      const out = ctx.createGain();
      out.gain.value = 0.5;
      const g = ctx.createGain();
      g.gain.value = 0.28;
      g.connect(out);
      const f = o.freq || 57;
      const a = ctx.createOscillator();
      a.frequency.value = f;
      const b = ctx.createOscillator();
      b.frequency.value = f * 1.006;
      const c = ctx.createOscillator();
      c.frequency.value = f * 2.7;
      const cg = ctx.createGain();
      cg.gain.value = 0.05;
      a.connect(g);
      b.connect(g);
      c.connect(cg);
      cg.connect(out);
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 0.06;
      const la = ctx.createGain();
      la.gain.value = 0.16;
      lfo.connect(la);
      la.connect(out.gain);
      a.start();
      b.start();
      c.start();
      lfo.start();
      return {
        out,
        stop() {
          [a, b, c, lfo].forEach(stopNode);
          try {
            out.disconnect();
          } catch (e) {}
        },
      };
    },

    /* Chisel-tick clusters and the occasional brush swish. */
    workshop(o) {
      o = o || {};
      const out = ctx.createGain();
      out.gain.value = 1;
      const kill = loopTimer(
        () => {
          if (Math.random() < (o.brushy == null ? 0.18 : o.brushy)) {
            swish(out, rnd(0.32, 0.5), 0.04);
            return;
          }
          const taps = 1 + Math.floor(Math.random() * 3);
          for (let i = 0; i < taps; i++) {
            tap(out, {
              freq: rnd(1900, 2600),
              q: 3,
              dur: 0.035,
              level: 0.045,
              attack: 0.002,
              at: i * rnd(0.15, 0.22),
            });
          }
        },
        o.minMs || 1500,
        o.maxMs || 4400,
        false
      );
      return {
        out,
        stop() {
          kill();
          try {
            out.disconnect();
          } catch (e) {}
        },
      };
    },
  };

  /* ---------------- scene recipes (data, not switch) ----------------
     Every name from REVISION_BRIEF §2a. v = layer mix level. */
  const L = (g, v, o) => ({ g, v, o });
  const SCENES = {
    'none': [],
    /* homepage film */
    'canvas': [
      L('wind', 0.3, { freq: 260, rate: 0.05 }),
      L('leaves', 0.08),
      L('workshop', 0.18, { brushy: 0.85, minMs: 7000, maxMs: 16000 }),
    ],
    'muse': [
      L('wind', 0.2, { freq: 300 }),
      L('lyre', 0.55, { minMs: 7000, maxMs: 15000 }),
      L('choir', 0.14),
      L('birds', 0.5),
    ],
    'workshop': [
      L('workshop', 0.6, { brushy: 0.25, minMs: 1600, maxMs: 4600 }),
      L('wind', 0.14, { freq: 220 }),
      L('stone', 0.16, { freq: 64 }),
    ],
    'gallery': [
      L('stone', 0.2, { freq: 60 }),
      L('choir', 0.16),
      L('lyre', 0.4, { minMs: 9000, maxMs: 19000 }),
      L('leaves', 0.05),
      L('wind', 0.1, { freq: 200 }),
    ],
    'landscape': [
      L('wind', 0.32, { freq: 340, depth: 0.45 }),
      L('birds', 0.7),
      L('leaves', 0.16),
      L('bees', 0.18),
      L('waves', 0.16),
    ],
    'sanctuary': [
      L('waves', 0.24),
      L('choir', 0.2),
      L('birds', 0.35),
      L('stone', 0.12, { freq: 52 }),
      L('wind', 0.16),
    ],
    /* the realm's areas */
    'realm-harbor': [
      L('waves', 0.5, { big: true }),
      L('wind', 0.26, { freq: 380 }),
      L('birds', 0.6, { gull: true }),
    ],
    'realm-grove': [
      L('leaves', 0.26),
      L('birds', 0.75),
      L('bees', 0.22),
      L('wind', 0.22),
    ],
    'realm-amphitheatre': [
      L('lyre', 0.65, { minMs: 5000, maxMs: 11000 }),
      L('wind', 0.18),
      L('choir', 0.12),
      L('birds', 0.3),
    ],
    'realm-temple': [
      L('stone', 0.34, { freq: 57 }),
      L('choir', 0.22),
      L('wind', 0.13, { freq: 240 }),
      L('birds', 0.15),
    ],
    'realm-garden': [
      L('bees', 0.3),
      L('birds', 0.65),
      L('leaves', 0.2),
      L('wind', 0.16, { freq: 300, rate: 0.06 }),
    ],
    'realm-sanctuary': [
      L('choir', 0.24),
      L('stone', 0.22, { freq: 52 }),
      L('waves', 0.12),
      L('wind', 0.16),
      L('lyre', 0.3, { minMs: 11000, maxMs: 22000 }),
    ],
    /* the mural-restoration studio */
    'muse-game': [
      L('lyre', 0.4, { minMs: 8000, maxMs: 18000, base: 220 }),
      L('wind', 0.18, { freq: 260 }),
      L('leaves', 0.09),
      L('workshop', 0.2, { brushy: 0.7, minMs: 5000, maxMs: 12000 }),
    ],
  };

  /* ---------------- bed lifecycle ---------------- */
  const bedLevel = () => 0.55 + 0.45 * intensity;

  /* Immediate full teardown of a retiring bed — timer cleared, every
     layer stopped, output disconnected. Used when too many beds fade. */
  const scrapRetiring = (rec) => {
    window.clearTimeout(rec.id);
    rec.b.layers.forEach((l) => {
      try {
        l.stop();
      } catch (e) {}
    });
    try {
      rec.b.out.disconnect();
    } catch (e) {}
    retiring = retiring.filter((r) => r !== rec);
  };

  const retire = (b, fade) => {
    /* at most two beds may fade at once; scrap the oldest beyond that */
    while (retiring.length >= 2) scrapRetiring(retiring[0]);
    ramp(b.out.gain, 0, fade);
    const rec = { b, id: 0 };
    rec.id = window.setTimeout(() => {
      b.layers.forEach((l) => {
        try {
          l.stop();
        } catch (e) {}
      });
      try {
        b.out.disconnect();
      } catch (e) {}
      retiring = retiring.filter((r) => r !== rec);
    }, fade * 1000 + 350);
    retiring.push(rec);
  };

  const applySceneNow = (name) => {
    if (!ctx) return;
    if (bed && bed.name === name) return;
    const fade = rnd(1.6, 2.8); /* §2a: 1.5–3 s crossfade */
    if (bed) retire(bed, fade);
    bed = null;
    const recipe = SCENES[name];
    if (!recipe || !recipe.length) return;
    const b = { name, out: ctx.createGain(), layers: [] };
    b.out.gain.value = 0;
    b.out.connect(bedBus);
    recipe.forEach((l) => {
      const make = GEN[l.g];
      if (!make) return;
      try {
        const layer = make(l.o || {});
        const lg = ctx.createGain();
        lg.gain.value = l.v;
        layer.out.connect(lg);
        lg.connect(b.out);
        b.layers.push(layer);
      } catch (e) {}
    });
    ramp(b.out.gain, 1, fade);
    bed = b;
  };

  /* Debounced entry: rapid scene flips (A/B/A…) coalesce to the latest
     requested name inside a ~250 ms window so beds and retirements never
     stack. When nothing is playing yet the first scene applies at once. */
  let sceneTimer = 0;
  let pendingScene = null;
  const applyScene = (name) => {
    if (!ctx) return;
    if (!bed && !sceneTimer) {
      applySceneNow(name);
      return;
    }
    pendingScene = name;
    if (sceneTimer) return; /* single timer; the latest name wins */
    sceneTimer = window.setTimeout(() => {
      sceneTimer = 0;
      const n = pendingScene;
      pendingScene = null;
      if (prefOn && ctx && n !== null) applySceneNow(n);
    }, 250);
  };

  /* ---------------- one-shot events ---------------- */
  const FOOT = {
    grass: { freq: 620, q: 0.7, dur: 0.09, level: 0.036, attack: 0.008 },
    stone: { freq: 1500, q: 1.4, dur: 0.05, level: 0.05, attack: 0.003, click: 3400 },
    sand: { freq: 850, q: 0.5, dur: 0.13, level: 0.04, attack: 0.012 },
    wood: { freq: 300, q: 1.1, dur: 0.07, level: 0.05, attack: 0.004, thump: 135 },
  };
  const footstep = (kind) => {
    const p = FOOT[kind];
    tap(fxBus, p);
    if (p.click) tap(fxBus, { freq: p.click, q: 2.5, dur: 0.02, level: 0.02, attack: 0.001 });
    if (p.thump) {
      const t0 = now();
      const osc = ctx.createOscillator();
      osc.frequency.setValueAtTime(p.thump, t0);
      osc.frequency.exponentialRampToValueAtTime(p.thump * 0.7, t0 + 0.09);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.04, t0);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.1);
      osc.connect(g);
      g.connect(fxBus);
      osc.start(t0);
      osc.stop(t0 + 0.12);
    }
  };

  const evReveal = () => {
    /* a soft bloom: three staggered high sines opening like paint. */
    [293.66, 440, 587.33].forEach((f, i) => {
      const t0 = now() + i * 0.07;
      const osc = ctx.createOscillator();
      osc.frequency.value = f * rnd(0.997, 1.003);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, t0);
      g.gain.linearRampToValueAtTime(0.028, t0 + 0.22);
      g.gain.linearRampToValueAtTime(0, t0 + 1.3);
      osc.connect(g);
      g.connect(fxBus);
      osc.start(t0);
      osc.stop(t0 + 1.4);
    });
  };

  const evDialogue = () => {
    /* a two-note question: root then a fourth above. */
    const i0 = 5 + Math.floor(Math.random() * 5);
    ksPluck(fxBus, noteHz(293.66, i0), 0.05, 2600, now());
    ksPluck(fxBus, noteHz(293.66, i0 + 3), 0.045, 2600, now() + 0.11);
  };

  const evBell = () => {
    const t0 = now();
    const base = rnd(520, 660);
    const car = ctx.createOscillator();
    car.frequency.value = base;
    const mod = ctx.createOscillator();
    mod.frequency.value = base * 2.76;
    const mg = ctx.createGain();
    mg.gain.setValueAtTime(base * 1.4, t0);
    mg.gain.exponentialRampToValueAtTime(1, t0 + 1.1);
    mod.connect(mg);
    mg.connect(car.frequency);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.05, t0);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.9);
    car.connect(g);
    g.connect(fxBus);
    car.start(t0);
    mod.start(t0);
    car.stop(t0 + 2);
    mod.stop(t0 + 2);
  };

  const evTransition = () => {
    /* a low painted swell carrying one scene into the next. */
    const t0 = now();
    const src = ctx.createBufferSource();
    src.buffer = getNoise();
    src.loop = true;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(280, t0);
    lp.frequency.linearRampToValueAtTime(760, t0 + 0.8);
    lp.frequency.linearRampToValueAtTime(240, t0 + 1.6);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(0.055, t0 + 0.7);
    g.gain.linearRampToValueAtTime(0, t0 + 1.6);
    src.connect(lp);
    lp.connect(g);
    g.connect(fxBus);
    src.start(t0, rnd(0, 1.5));
    window.setTimeout(() => stopNode(src), 1900);
    const osc = ctx.createOscillator();
    osc.frequency.value = 82;
    const og = ctx.createGain();
    og.gain.setValueAtTime(0, t0);
    og.gain.linearRampToValueAtTime(0.03, t0 + 0.7);
    og.gain.linearRampToValueAtTime(0, t0 + 1.5);
    osc.connect(og);
    og.connect(fxBus);
    osc.start(t0);
    osc.stop(t0 + 1.6);
  };

  const EVENTS = {
    'brush': { gap: 150, fn: () => swish(fxBus, rnd(0.28, 0.45), 0.05) },
    'reveal': { gap: 420, fn: evReveal },
    'dialogue': { gap: 320, fn: evDialogue },
    'transition': { gap: 1000, fn: evTransition },
    'bird': { gap: 550, fn: () => chirp(fxBus, { level: 0.05 }) },
    'bell': { gap: 650, fn: evBell },
    'pluck': {
      gap: 220,
      fn: () => ksPluck(fxBus, noteHz(146.83, 4 + Math.floor(Math.random() * 10)), 0.07, 2400, now()),
    },
    'wave': { gap: 1400, fn: () => seaSwell(fxBus, { level: 0.06 }) },
    'footstep-grass': { gap: 170, fn: () => footstep('grass') },
    'footstep-stone': { gap: 170, fn: () => footstep('stone') },
    'footstep-sand': { gap: 170, fn: () => footstep('sand') },
    'footstep-wood': { gap: 170, fn: () => footstep('wood') },
  };
  const lastFired = {};

  /* ---------------- graph + lifecycle ---------------- */

  /* The output core: compressor -> tanh soft-clip -> master gain.
     The clipper is a hard ceiling — whatever any generator ever does,
     nothing beyond a gently saturated full-scale can reach the ear. */
  const buildCore = () => {
    try {
      if (master) master.disconnect();
    } catch (e) {}
    try {
      if (compNode) compNode.disconnect();
    } catch (e) {}
    try {
      if (clipNode) clipNode.disconnect();
    } catch (e) {}
    compNode = ctx.createDynamicsCompressor();
    compNode.threshold.value = -26;
    compNode.knee.value = 24;
    compNode.ratio.value = 5;
    compNode.attack.value = 0.008;
    compNode.release.value = 0.35;
    if (!clipCurve) {
      clipCurve = new Float32Array(1024);
      const k = Math.tanh(1.6);
      for (let i = 0; i < 1024; i++) {
        const x = i / 511.5 - 1;
        clipCurve[i] = Math.tanh(x * 1.6) / k;
      }
    }
    clipNode = ctx.createWaveShaper();
    clipNode.curve = clipCurve;
    clipNode.oversample = '2x';
    master = ctx.createGain();
    master.gain.value = 0;
    compNode.connect(clipNode);
    clipNode.connect(master);
    master.connect(ctx.destination);
    guardAn = ctx.createAnalyser();
    guardAn.fftSize = 512;
    guardBuf = new Float32Array(512);
    compNode.connect(guardAn);
  };

  /* Emergency stop. If the pre-clip mix ever runs away or turns non-finite
     (a synthesis bug, a browser DSP quirk), silence lands within a beat:
     every live bed and bus is scrapped, the output core is rebuilt clean,
     and the current scene fades back in a moment later. */
  const panic = () => {
    try {
      master.gain.cancelScheduledValues(now());
      master.gain.setValueAtTime(0, now());
    } catch (e) {}
    while (retiring.length) scrapRetiring(retiring[0]);
    if (bed) {
      bed.layers.forEach((l) => {
        try {
          l.stop();
        } catch (e) {}
      });
      try {
        bed.out.disconnect();
      } catch (e) {}
      bed = null;
    }
    try {
      bedBus.disconnect();
    } catch (e) {}
    try {
      fxBus.disconnect();
    } catch (e) {}
    buildCore();
    bedBus = ctx.createGain();
    bedBus.gain.value = bedLevel();
    fxBus = ctx.createGain();
    fxBus.gain.value = 0.9;
    bedBus.connect(compNode);
    fxBus.connect(compNode);
    guardHot = 0;
    window.setTimeout(() => {
      if (prefOn && ctx) {
        ramp(master.gain, MASTER_LEVEL, 1.2);
        applySceneNow(currentScene);
      }
    }, 700);
  };

  const buildGraph = () => {
    ctx = new AC();
    buildCore();
    bedBus = ctx.createGain();
    bedBus.gain.value = bedLevel();
    fxBus = ctx.createGain();
    fxBus.gain.value = 0.9;
    bedBus.connect(compNode);
    fxBus.connect(compNode);
    if (!guardTimer) {
      guardTimer = window.setInterval(() => {
        if (!ctx || ctx.state !== 'running' || !guardAn) return;
        try {
          guardAn.getFloatTimeDomainData(guardBuf);
          let s = 0;
          for (let i = 0; i < guardBuf.length; i++) s += guardBuf[i] * guardBuf[i];
          const rms = Math.sqrt(s / guardBuf.length);
          if (!isFinite(rms) || rms > 1.4) {
            guardHot += 1;
            if (guardHot >= 2) panic();
          } else {
            guardHot = 0;
          }
        } catch (e) {}
      }, 350);
    }
  };

  const startAudio = () => {
    if (!AC || !prefOn) return;
    if (!ctx) buildGraph();
    if (ctx.state !== 'running') quietPromise(ctx.resume());
    ramp(master.gain, MASTER_LEVEL, 0.8);
    applyScene(currentScene);
  };

  const syncUi = () => {
    const on = !!(prefOn && AC);
    try {
      rootEl.classList.toggle('atl-audio-on', on);
      doc.querySelectorAll('[data-atl-audio-toggle]').forEach((b) => {
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    } catch (e) {}
  };

  /* ---------------- public API (never throws) ---------------- */
  const api = {
    ready: true,
    enabled() {
      try {
        return running();
      } catch (e) {
        return false;
      }
    },
    toggle() {
      try {
        if (!AC) return;
        prefOn = !prefOn;
        writePref(prefOn);
        gestured = true; /* toggle is only reachable via a real gesture */
        if (prefOn) {
          startAudio();
        } else if (ctx) {
          ramp(master.gain, 0, 0.35);
          window.setTimeout(() => {
            if (!prefOn && ctx && ctx.state === 'running') quietPromise(ctx.suspend());
          }, 480);
        }
        syncUi();
        announce(prefOn ? 'Sound on' : 'Sound off');
      } catch (e) {}
    },
    setScene(name) {
      try {
        const n = typeof name === 'string' && Object.prototype.hasOwnProperty.call(SCENES, name) ? name : 'none';
        currentScene = n;
        if (prefOn && ctx) applyScene(n);
      } catch (e) {}
    },
    setIntensity(v) {
      try {
        intensity = clamp01(v);
        if (bedBus) bedBus.gain.setTargetAtTime(bedLevel(), now(), 0.7);
      } catch (e) {}
    },
    event(name) {
      try {
        if (!running()) return;
        const e = EVENTS[name];
        if (!e) return;
        const t = performance.now();
        if (t - (lastFired[name] || 0) < e.gap) return;
        lastFired[name] = t;
        e.fn();
      } catch (e) {}
    },
  };
  window.AtelierAudio = api;

  /* ---------------- wiring ---------------- */
  /* First real gesture: if the stored preference is 'on', wake audio.
     Not every gesture grants autoplay activation (touchstart, a
     touch-derived pointerdown, an Escape keydown…), so we also listen
     on the granting end of each gesture (click, touchend, pointerup,
     keyup), try to resume on every fire, and only stand down once the
     context is genuinely running. */
  const GESTURES = ['pointerdown', 'keydown', 'touchstart', 'click', 'touchend', 'pointerup', 'keyup'];
  const detachGestures = () => {
    GESTURES.forEach((t) => doc.removeEventListener(t, onGesture, true));
  };
  const onGesture = () => {
    gestured = true;
    if (!AC || !prefOn) {
      /* nothing to wake — opting in later goes through toggle() */
      detachGestures();
      return;
    }
    try {
      startAudio();
    } catch (e) {}
    if (!ctx) return;
    if (ctx.state === 'running') {
      detachGestures();
      return;
    }
    /* resume() doubles as a state probe: it resolves once running. */
    try {
      const p = ctx.resume();
      if (p && typeof p.then === 'function') {
        p.then(() => {
          if (ctx && ctx.state === 'running') detachGestures();
        }).catch(() => {});
      }
    } catch (e) {}
  };
  GESTURES.forEach((t) => doc.addEventListener(t, onGesture, { capture: true, passive: true }));

  /* The painted toggle(s): event delegation so any number of
     [data-atl-audio-toggle] buttons work, whenever they render. */
  doc.addEventListener('click', (e) => {
    const t = e.target;
    const btn = t && t.closest ? t.closest('[data-atl-audio-toggle]') : null;
    if (btn) api.toggle();
  });

  doc.addEventListener('visibilitychange', () => {
    if (!ctx) return;
    try {
      if (doc.hidden) quietPromise(ctx.suspend());
      else if (prefOn && gestured) quietPromise(ctx.resume());
    } catch (e) {}
  });

  /* Theme Editor may inject toggle buttons after load. */
  doc.addEventListener('shopify:section:load', syncUi);
  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', syncUi, { once: true });
  } else {
    syncUi();
  }
})();
